import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { authAPI } from '../services/api';
import { track } from '../utils/analytics';
import { AnalyticsEvents } from '../utils/analyticsEvents';

interface TourContextType {
  isTourActive: boolean;
  startTour: () => void;
  stopTour: () => void;
  skipTour: () => void;
  run: boolean;
  stepIndex: number;
  setStepIndex: (index: number) => void;
  tourKey: number;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

interface TourProviderProps {
  children: ReactNode;
}

/** localStorage key: auto-shown once per user per device so we don't re-auto-start on every dashboard visit. */
const TOUR_AUTO_SHOWN_KEY = 'cold_caller_tour_auto_shown';
const getAutoShownKey = (userId: string | undefined) =>
  userId ? `${TOUR_AUTO_SHOWN_KEY}_${userId}` : TOUR_AUTO_SHOWN_KEY;

const SHOW_TOUR_AFTER_SIGNUP_KEY = 'cold_caller_show_tour';

export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [isTourActive, setIsTourActive] = useState(false);
  const [tourKey, setTourKey] = useState(0);
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const tourScheduledRef = useRef(false);

  const startTour = React.useCallback(() => {
    setIsTourActive(false);
    setRun(false);
    setStepIndex(0);
    setTourKey((prev) => prev + 1);
    track(AnalyticsEvents.TOUR_START);
    setTimeout(() => {
      setIsTourActive(true);
      setRun(true);
      setStepIndex(0);
    }, 100);
  }, []);

  // Auto-start tour once per user on dashboard: only if not already finished (DB) and not auto-shown this device (localStorage)
  useEffect(() => {
    if (!user || isTourActive || run) return;
    if (tourScheduledRef.current) return;
    if (user.onboarding_tour_finished_at) return;
    if (localStorage.getItem(getAutoShownKey(user.id)) === 'true') return;
    if (location.pathname !== '/dashboard') return;

    tourScheduledRef.current = true;
    const timer = setTimeout(() => {
      sessionStorage.removeItem(SHOW_TOUR_AFTER_SIGNUP_KEY);
      localStorage.setItem(getAutoShownKey(user.id), 'true');
      tourScheduledRef.current = false;
      startTour();
    }, 1200);
    return () => {
      clearTimeout(timer);
      if (!run) tourScheduledRef.current = false;
    };
  }, [user, isTourActive, run, startTour, location.pathname]);

  const stopTour = async () => {
    setIsTourActive(false);
    setRun(false);
    track(AnalyticsEvents.TOUR_COMPLETE, { last_step_index: stepIndex });
    try {
      await authAPI.setTourFinished();
      await refreshUser();
    } catch {
      // Offline or API error; user can retry or tour state will sync on next load
    }
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
  };

  const skipTour = async () => {
    setIsTourActive(false);
    setRun(false);
    track(AnalyticsEvents.TOUR_SKIP, { last_step_index: stepIndex });
    try {
      await authAPI.setTourFinished();
      await refreshUser();
    } catch {
      // Offline or API error
    }
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
  };

  const handleSetStepIndex = React.useCallback((index: number) => {
    setStepIndex(index);
    if (isTourActive) {
      track(AnalyticsEvents.TOUR_STEP_VIEW, { step_index: index });
    }
  }, [isTourActive]);

  const value = {
    isTourActive,
    startTour,
    stopTour,
    skipTour,
    run,
    stepIndex,
    setStepIndex: handleSetStepIndex,
    tourKey,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};
